/**
 * AI Agent Page.
 * Autonomous agent for analyzing and manipulating Excel workbooks.
 */

import React, { useState, useCallback } from 'react';
import type { AgentPlan, AgentResult } from '@excel-ai-studio/shared';
import {
  Button,
  Input,
  Card,
  CardHeader,
  CardPreview,
  Body1,
  Caption1,
} from '@fluentui/react-components';
import { PlayRegular } from '@fluentui/react-icons';
import { runAgentGoal } from '../services/api.client';
import { readSelectionInfo } from '../office';
import { AgentPlanPanel } from '../components/features/AgentPlanPanel';
import { AgentExecutionPanel } from '../components/features/AgentExecutionPanel';
import { AgentResultPanel } from '../components/features/AgentResultPanel';
import { ErrorDisplay } from '../components/common/ErrorDisplay';
import { LoadingSpinner } from '../components/common/LoadingSpinner';

export const AIAgentPage: React.FC = () => {
  const [objective, setObjective] = useState('');
  const [plan, setPlan] = useState<AgentPlan | null>(null);
  const [result, setResult] = useState<AgentResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handlePlanAndExecute = useCallback(async () => {
    if (!objective.trim()) {
      setError('Veuillez entrer un objectif');
      return;
    }

    setIsLoading(true);
    setError(null);
    setPlan(null);
    setResult(null);

    try {
      // Read selection from Excel
      const selectionInfo = await readSelectionInfo();

      if (!selectionInfo.values || selectionInfo.values.length === 0) {
        setError('Veuillez sélectionner des données dans Excel');
        return;
      }

      // Extract headers from first row
      const headers = selectionInfo.values[0]?.map((v) =>
        typeof v === 'string' ? v : String(v),
      ) || [];

      // Run the full agent workflow
      const response = await runAgentGoal({
        goal: {
          objective,
          language: 'fr',
        },
        selectionInfo: {
          values: selectionInfo.values,
          headers,
          address: selectionInfo.address,
        },
      });

      setResult(response.result);
      setPlan(response.result.plan);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  }, [objective]);

  return (
    <div className="ai-agent-page">
      <div className="agent-input-section">
        <Card>
          <CardHeader header={<Body1 style={{ fontWeight: 600 }}>Agent IA</Body1>} />
          <CardPreview>
            <div className="agent-input-content">
              <Caption1>Que veux-tu faire dans ce fichier ?</Caption1>
              <Input
                placeholder="Ex: Analyse ce dataset et crée un dashboard"
                value={objective}
                onChange={(e) => setObjective(e.target.value)}
                disabled={isLoading}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !isLoading) {
                    handlePlanAndExecute();
                  }
                }}
              />
              <Button
                appearance="primary"
                icon={<PlayRegular />}
                onClick={handlePlanAndExecute}
                disabled={isLoading || !objective.trim()}
              >
                {isLoading ? 'Analyse en cours...' : 'Analyser'}
              </Button>
            </div>
          </CardPreview>
        </Card>
      </div>

      {error && <ErrorDisplay message={error} />}

      {isLoading && (
        <div className="agent-loading">
          <LoadingSpinner label="Analyse en cours..." />
        </div>
      )}

      {plan && !isLoading && <AgentPlanPanel plan={plan} />}

      {result && !isLoading && (
        <>
          <AgentExecutionPanel execution={result.execution} />
          <AgentResultPanel result={result} />
        </>
      )}

      {!isLoading && !plan && !result && !error && (
        <Card>
          <CardPreview>
            <div className="agent-empty-state">
              <Caption1>Sélectionnez des données et décrivez ce que vous voulez faire.</Caption1>
            </div>
          </CardPreview>
        </Card>
      )}
    </div>
  );
};
